import {
  CellStyleModule,
  ClientSideRowModelModule,
  LocaleModule,
  ModuleRegistry,
  PaginationModule,
  RowSelectionModule,
  RowStyleModule,
  TooltipModule,
  ValidationModule,
  colorSchemeDark,
  themeQuartz,
  type ColDef,
  type GridOptions,
  type RowSelectionOptions,
  type SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeProvider';

// Only what the tables use: AllCommunityModule alone was a megabyte of the bundle. ValidationModule names any module
// a new grid option needs, in development and in the tests; the production build drops it.
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  PaginationModule,
  TooltipModule,
  CellStyleModule,
  RowStyleModule,
  RowSelectionModule,
  LocaleModule,
  ...(import.meta.env.DEV ? [ValidationModule] : []),
]);

/*
 * Written out rather than read from the CSS variables, because ag-grid's theming API takes values and not var()
 * references - and both are built at module level, since a new object per render makes the grid re-read every
 * column definition.
 */
const gridTheme = themeQuartz.withParams({
  accentColor: '#06b6d4',
  borderColor: '#dfe0e1',
  fontFamily: 'inherit',
  headerBackgroundColor: '#f8f9fa',
  headerTextColor: '#6b7280',
  oddRowBackgroundColor: '#f8f9fa',
  rowHoverColor: '#ecfeff',
  wrapperBorderRadius: '6px',
});

const darkGridTheme = themeQuartz.withPart(colorSchemeDark).withParams({
  accentColor: '#06b6d4',
  backgroundColor: '#1e2126',
  borderColor: '#32373d',
  fontFamily: 'inherit',
  foregroundColor: '#e7e9ec',
  headerBackgroundColor: '#24282e',
  headerTextColor: '#a2aab4',
  oddRowBackgroundColor: '#22262b',
  rowHoverColor: '#0c343d',
  wrapperBorderRadius: '6px',
});

/*
 * Not an object literal in the JSX below: ag-grid re-processes every column definition when a grid option it
 * holds changes identity, which made the table blink on every keystroke in the search box.
 */
const DEFAULT_COLUMN: ColDef = { sortable: true, resizable: true, filter: false };

/** Offered beside the page number. The default has to be one of them, or ag-grid says so in the console. */
const PAGE_SIZES = [10, 25, 50, 100];

const MULTI_ROW_SELECTION: RowSelectionOptions = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  selectAll: 'currentPage',
};

function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;

  return ref;
}

export interface DataGridProps<TRow> {
  rows: TRow[];
  columns: ColDef<TRow>[];
  /** Stable identity, so a refreshed list re-renders the cells that changed and nothing else. */
  getRowId: (row: TRow) => string;
  label: string;
  emptyMessage: string;
  loading?: boolean;
  pageSize?: number;
  rowHeight?: number;
  getRowClass?: GridOptions<TRow>['getRowClass'];
  postSortRows?: GridOptions<TRow>['postSortRows'];
  getRowHeight?: (row: TRow) => number | undefined;
  onSelectionChange?: (rows: TRow[]) => void;
}

export function DataGrid<TRow>({
  rows,
  columns,
  getRowId,
  label,
  emptyMessage,
  loading = false,
  pageSize,
  rowHeight = 48,
  getRowClass,
  postSortRows,
  getRowHeight,
  onSelectionChange,
}: DataGridProps<TRow>) {
  const { t } = useTranslation();
  const { theme } = useTheme();

  // The same reasoning as the column defaults, for the callbacks: one stable function each, so the callers do
  // not have to memoise their own.
  const latestRowId = useLatest(getRowId);
  const latestRowClass = useLatest(getRowClass);
  const latestPostSort = useLatest(postSortRows);
  const latestSelectionChange = useLatest(onSelectionChange);
  const latestRowHeight = useLatest(getRowHeight);

  const rowHeightOf = useCallback<NonNullable<GridOptions<TRow>['getRowHeight']>>(
    ({ data }) => (data === undefined ? undefined : latestRowHeight.current?.(data)),
    [latestRowHeight],
  );

  const rowId = useCallback<NonNullable<GridOptions<TRow>['getRowId']>>(
    ({ data }) => latestRowId.current(data),
    [latestRowId],
  );

  const rowClass = useCallback<NonNullable<GridOptions<TRow>['getRowClass']>>(
    (params) => latestRowClass.current?.(params),
    [latestRowClass],
  );

  const sortRows = useCallback<NonNullable<GridOptions<TRow>['postSortRows']>>(
    (params) => latestPostSort.current?.(params),
    [latestPostSort],
  );

  const selectionChanged = useCallback(
    ({ api }: SelectionChangedEvent<TRow>) => latestSelectionChange.current?.(api.getSelectedRows()),
    [latestSelectionChange],
  );

  const selectable = onSelectionChange !== undefined;

  // Memoised because handing the grid a new locale object would have it re-read everything.
  const localeText = useMemo(
    () => ({
      page: t('grid.page'),
      to: t('grid.to'),
      of: t('grid.of'),
      firstPage: t('grid.firstPage'),
      previousPage: t('grid.previousPage'),
      nextPage: t('grid.nextPage'),
      lastPage: t('grid.lastPage'),
      pageSizeSelectorLabel: t('grid.pageSize'),
      ariaPageSizeSelectorLabel: t('grid.pageSize'),
    }),
    [t],
  );

  // ag-grid's no-rows overlay is absolutely positioned over the row area, so it cannot make room for itself:
  // a message of a sentence or two was clipped by the height of one row. This one is part of the document.
  const empty = !loading && rows.length === 0;

  return (
    <div
      className={`app-grid${empty ? ' app-grid--empty' : ''}`}
      role="region"
      aria-label={label}
      aria-busy={loading}
    >
      <AgGridReact<TRow>
        theme={theme === 'dark' ? darkGridTheme : gridTheme}
        rowData={rows}
        columnDefs={columns}
        getRowId={rowId}
        getRowClass={rowClass}
        postSortRows={sortRows}
        getRowHeight={getRowHeight ? rowHeightOf : undefined}
        rowSelection={selectable ? MULTI_ROW_SELECTION : undefined}
        onSelectionChanged={selectable ? selectionChanged : undefined}
        defaultColDef={DEFAULT_COLUMN}
        domLayout="autoHeight"
        pagination={pageSize !== undefined}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={PAGE_SIZES}
        localeText={localeText}
        rowHeight={rowHeight}
        headerHeight={40}
        suppressCellFocus
        enableCellTextSelection
        ensureDomOrder
        // Ours is below, in the flow. The loading overlay stays ag-grid's: it covers the rows it replaces.
        suppressNoRowsOverlay
        overlayLoadingTemplate={`<span class="grid-empty">${escapeHtml(t('app.loading'))}</span>`}
        loading={loading}
      />

      {empty && <p className="grid-empty">{emptyMessage}</p>}
    </div>
  );
}

/** The overlay templates are raw HTML, and the messages are translated text that may contain quotes. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
